/* ============================================================
   Pentagon Pizza Index — Real-time pizza busyness near Pentagon
   Data source: pizzint.watch API (Google Maps busyness scraper)
   ============================================================ */
import { h } from "@/utils/dom";
import { timeAgo } from "@/utils/dom";
const API_URL = "/api/pizzint";
const POLL_INTERVAL = 5 * 60 * 1000; // 5 minutes
const DOUGHCON_LABELS = {
    1: { label: "COCKED PISTOL", color: "var(--threat-critical)" },
    2: { label: "FAST PACE", color: "var(--threat-high)" },
    3: { label: "ROUND HOUSE", color: "var(--threat-elevated)" },
    4: { label: "DOUBLE TAKE", color: "var(--status-active)" },
    5: { label: "FADE OUT", color: "var(--text-dim)" },
};
export class PizzaIndexPopup {
    el;
    overlay;
    contentEl;
    headerBtn;
    defconBadge;
    indexLabel;
    visible = false;
    pollTimer = null;
    data = null;
    constructor() {
        this.createHeaderButton();
        this.createPopup();
        this.fetchData();
        this.pollTimer = setInterval(() => this.fetchData(), POLL_INTERVAL);
    }
    getHeaderButton() {
        return this.headerBtn;
    }
    createHeaderButton() {
        this.defconBadge = h("span", { className: "pizza-defcon-badge" }, "DoughCon —");
        this.indexLabel = h("span", { className: "pizza-index-label" }, "—%");
        this.headerBtn = h("button", {
            className: "pizza-header-btn",
            onClick: () => this.toggle(),
        }, h("span", { className: "pizza-icon" }, "🍕"), this.defconBadge, this.indexLabel);
    }
    createPopup() {
        this.overlay = h("div", { className: "pizza-overlay hidden" });
        this.overlay.addEventListener("click", () => this.hide());
        this.el = h("div", { className: "pizza-popup hidden" });
        this.contentEl = h("div", { className: "pizza-popup-content" });
        const header = h("div", { className: "pizza-popup-header" }, h("span", null, "Pentagon Pizza Index — DoughCon"), h("button", {
            className: "pizza-close-btn",
            onClick: () => this.hide(),
        }, "✕"));
        this.el.append(header, this.contentEl);
        document.body.append(this.overlay, this.el);
    }
    async fetchData() {
        try {
            const res = await fetch(`${API_URL}?_t=${Date.now()}`);
            if (!res.ok)
                throw new Error(`HTTP ${res.status}`);
            this.data = await res.json();
            this.updateHeaderButton();
            if (this.visible)
                this.renderContent();
        }
        catch (err) {
            console.error("Pizza Index fetch error:", err);
        }
    }
    updateHeaderButton() {
        if (!this.data)
            return;
        const level = this.data.defcon_level ?? 5;
        const info = DOUGHCON_LABELS[level] ?? DOUGHCON_LABELS[5];
        this.defconBadge.textContent = `DoughCon ${level}`;
        this.defconBadge.style.background = info.color;
        this.indexLabel.textContent = `${this.data.overall_index ?? 0}%`;
    }
    toggle() {
        if (this.visible) {
            this.hide();
        }
        else {
            this.show();
        }
    }
    show() {
        this.visible = true;
        this.el.classList.remove("hidden");
        this.overlay.classList.remove("hidden");
        this.renderContent();
    }
    hide() {
        this.visible = false;
        this.el.classList.add("hidden");
        this.overlay.classList.add("hidden");
    }
    renderContent() {
        this.contentEl.innerHTML = "";
        if (!this.data) {
            this.contentEl.appendChild(h("div", { className: "pizza-loading" }, "SCANNING PIZZA FREQUENCIES..."));
            return;
        }
        const level = this.data.defcon_level ?? 5;
        const info = DOUGHCON_LABELS[level] ?? DOUGHCON_LABELS[5];
        // DEFCON subtitle
        const subtitle = h("div", { className: "pizza-defcon-subtitle" });
        subtitle.style.color = info.color;
        subtitle.textContent = `${info.label} — ${level === 1 ? "MAXIMUM READINESS" : level === 2 ? "HIGH ALERT" : level === 3 ? "ELEVATED ACTIVITY" : level === 4 ? "INCREASED INTELLIGENCE WATCH" : "NORMAL OPERATIONS"}`;
        this.contentEl.appendChild(subtitle);
        // Store list
        const list = h("div", { className: "pizza-store-list" });
        // Deduplicate by name (API sometimes has dupes)
        const seen = new Set();
        for (const store of this.data.data) {
            const key = store.place_id;
            if (seen.has(key))
                continue;
            seen.add(key);
            list.appendChild(this.renderStore(store));
        }
        this.contentEl.appendChild(list);
        // Footer
        const updatedAt = this.data.timestamp
            ? timeAgo(new Date(this.data.timestamp))
            : "—";
        const footer = h("div", { className: "pizza-footer" }, h("a", {
            className: "pizza-source-link",
            href: "https://www.pizzint.watch/",
            target: "_blank",
        }, "Source: PizzINT"), h("span", { className: "pizza-updated" }, `Updated ${updatedAt}`));
        this.contentEl.appendChild(footer);
    }
    renderStore(store) {
        const row = h("div", { className: "pizza-store-row" });
        const nameEl = h("span", { className: "pizza-store-name" }, store.name);
        let badgeClass = "pizza-status-badge";
        let badgeText = "";
        if (store.current_popularity === null ||
            store.current_popularity === undefined) {
            badgeClass += " badge-closed";
            badgeText = "CLOSED";
        }
        else if (store.is_spike) {
            badgeClass += " badge-spike";
            badgeText = `SPIKE ${store.percentage_of_usual ?? store.current_popularity}%`;
        }
        else if (store.current_popularity === 0) {
            badgeClass += " badge-quiet";
            badgeText = "QUIET 0%";
        }
        else {
            badgeClass += " badge-quiet";
            badgeText = `QUIET ${store.percentage_of_usual ?? store.current_popularity}%`;
        }
        const badge = h("span", { className: badgeClass }, badgeText);
        row.append(nameEl, badge);
        return row;
    }
    destroy() {
        if (this.pollTimer)
            clearInterval(this.pollTimer);
        this.el.remove();
        this.overlay.remove();
    }
}
